import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import AppLayout from './components/layout/AppLayout';
import ChannelView from './pages/ChannelView';
import InviteModal from './components/invite/InviteModal';
import GetStartedChecklist from './components/onboarding/GetStartedChecklist';
import { useAuthStore } from './stores/authStore';
import { useSidebar } from './lib/sidebarContext';
import { useSilentRefresh } from './hooks/useSilentRefresh';
import api from './lib/api';
import { getDefaultOrgId } from './lib/default-org';
import { LogoGlyph } from './components/ui/Logo';
import UpdateRequiredScreen from './components/UpdateRequiredScreen';
import { useUpdateRequiredStore } from './lib/client-version';

// Code-split the guest pages as well as the secondary ones. A logged-in user
// never sees Login/Register/Join again, and an unauthenticated visitor never
// sees Channel/Settings/etc., so keeping the two paths separate shaves
// ~80 kB gzip off whichever bundle the visitor actually downloads first.
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const JoinPage = lazy(() => import('./pages/JoinPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const TodoPage = lazy(() => import('./pages/TodoPage'));
const RecentFilesPage = lazy(() => import('./pages/RecentFilesPage'));
const ExtensionPage = lazy(() => import('./pages/ExtensionPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));

function LazyFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
  );
}

export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  // Gate on the persisted `hasSession` flag — a boolean that survives reload
  // without storing any PII in localStorage (APP 11). The full user object
  // is populated from /api/v1/users/me after silent refresh succeeds.
  const hasSession = useAuthStore((s) => s.hasSession);
  if (!hasSession) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const hasSession = useAuthStore((s) => s.hasSession);
  if (hasSession) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export default function App() {
  useSilentRefresh();
  const updateRequired = useUpdateRequiredStore((s) => s.required);

  // The server refuses this bundle as too old: nothing else can work.
  if (updateRequired) return <UpdateRequiredScreen />;

  return (
    <>
      <Routes>
        <Route
          path="/login"
          element={
            <GuestRoute>
              <Suspense fallback={<LazyFallback />}>
                <LoginPage />
              </Suspense>
            </GuestRoute>
          }
        />
        <Route
          path="/register"
          element={
            <GuestRoute>
              <Suspense fallback={<LazyFallback />}>
                <RegisterPage />
              </Suspense>
            </GuestRoute>
          }
        />
        <Route
          path="/join"
          element={
            <Suspense fallback={<LazyFallback />}>
              <JoinPage />
            </Suspense>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <GuestRoute>
              <Suspense fallback={<LazyFallback />}>
                <ForgotPasswordPage />
              </Suspense>
            </GuestRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <Suspense fallback={<LazyFallback />}>
              <ResetPasswordPage />
            </Suspense>
          }
        />
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<LazyFallback />}>
              <PrivacyPolicyPage />
            </Suspense>
          }
        />
        <Route
          path="/terms"
          element={
            <Suspense fallback={<LazyFallback />}>
              <TermsOfServicePage />
            </Suspense>
          }
        />
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path="settings"
            element={
              <Suspense fallback={<LazyFallback />}>
                <SettingsPage />
              </Suspense>
            }
          />
          <Route
            path="todos"
            element={
              <Suspense fallback={<LazyFallback />}>
                <TodoPage />
              </Suspense>
            }
          />
          <Route
            path="recent-files"
            element={
              <Suspense fallback={<LazyFallback />}>
                <RecentFilesPage />
              </Suspense>
            }
          />
          <Route path=":orgSlug/:channelId" element={<ChannelView />} />
          <Route path=":orgSlug" element={<WelcomeScreen />} />
          <Route index element={<WelcomeScreen />} />
        </Route>
        <Route
          path="/extension"
          element={
            <Suspense fallback={<LazyFallback />}>
              <ExtensionPage />
            </Suspense>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}

interface OrgOption {
  id: string;
  name: string;
  slug: string;
  role: string;
}

function WelcomeScreen() {
  const [showInvite, setShowInvite] = useState(false);
  const { openSidebar } = useSidebar();
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const user = useAuthStore((s) => s.user);

  const { data: orgs } = useQuery<OrgOption[]>({
    queryKey: ['orgs'],
    queryFn: async () => {
      const { data } = await api.get('/api/v1/orgs');
      return data.data;
    },
  });

  // Landing on bare /app with a saved default workspace: jump straight into
  // it (first channel when one exists). Guarded by a ref so a slow channels
  // fetch can't double-navigate, and only when the URL names no workspace —
  // visiting /app/:orgSlug explicitly must never bounce to the default.
  const redirectedRef = useRef(false);
  useEffect(() => {
    if (orgSlug || redirectedRef.current || !orgs) return;
    const defaultOrg = orgs.find((o) => o.id === getDefaultOrgId(user?.id));
    if (!defaultOrg) return;
    redirectedRef.current = true;
    api
      .get(`/api/v1/orgs/${defaultOrg.id}/channels`)
      .then(({ data }) => {
        const first = (data.data as { id: string }[])[0];
        navigate(first ? `/app/${defaultOrg.slug}/${first.id}` : `/app/${defaultOrg.slug}`, {
          replace: true,
        });
      })
      .catch(() => navigate(`/app/${defaultOrg.slug}`, { replace: true }));
  }, [orgSlug, orgs, user?.id, navigate]);

  const adminOrg = orgs?.find((o) => o.role === 'OWNER' || o.role === 'ADMIN');

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center border-b border-gray-200 px-3 py-3 md:hidden">
        <button
          onClick={openSidebar}
          className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          aria-label="Open menu"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
        <span className="ml-2 text-sm font-semibold text-gray-700">ClinikChat</span>
      </div>
      <div className="flex flex-1 items-center justify-center p-8">
        <div className="w-full max-w-sm text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-praxentis-navy/10 text-labs-400 dark:bg-primary/20">
            <LogoGlyph className="h-8 w-8" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Welcome to ClinikChat</h2>
          {/* Mobile has no sidebar, only a drawer, so "menu" is correct on both layouts. */}
          <p className="mt-2 text-sm text-gray-400 dark:text-slate-400">
            Open the menu to pick a group, or create one to get started.
          </p>

          <GetStartedChecklist
            onInvite={() => setShowInvite(true)}
            onCreateGroup={openSidebar}
            onGoToSettings={() => navigate('/app/settings')}
          />

          {adminOrg && (
            <InviteModal
              orgId={adminOrg.id}
              orgName={adminOrg.name}
              open={showInvite}
              onClose={() => setShowInvite(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}
